import styled from 'styled-components';

import { HOME, LUMIO, SPACE } from '../../../../theme/tokens';
import { pulseCard } from '../../../../theme/mixins';
import { Text } from '../../../../components';

/** The overview card: the sentences, then the figures, one gap apart. */
export const Frame = styled.div`
  ${pulseCard}
  display: flex;
  flex-direction: column;
  gap: ${SPACE[16]};
  padding: ${SPACE[12]};
`;

export const Lead = styled(Text)`
  margin: ${SPACE[0]};
  font-size: 20px;
  font-weight: 600;
  line-height: 1.35;
  color: ${HOME.t50};
`;

export const Follow = styled(Text)`
  margin: ${SPACE[0]};
  font-size: 15px;
  line-height: 1.5;
  color: ${HOME.t300};
`;

/** What the numbers are narrowed to: quiet labelled items on one line (wrapping on a phone). */
export const Context = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACE[4]} ${SPACE[16]};
  font-size: 13px;
  color: ${HOME.t500};
`;

/** The narrowed-to filters beside one picture (the first filter's). */
export const ContextBlock = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${SPACE[12]};
  min-width: 0;
  width: 100%;
`;

export const ContextList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  min-width: 0;
`;

/** One narrowed-to filter, each part on its own row: its label, its name (a link), then a tracking link's address. */
export const ContextItem = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${SPACE[2]};
  min-width: 0;
`;

/** The address and its copy button, kept together. */
export const ContextUrlRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[8]};
  min-width: 0;
  max-width: 100%;
`;

/** The narrowed-to name: a link (out to a website, or to the asset in Marketing) or plain text. */
export const ContextName = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[4]};
  color: ${HOME.t50};
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  background: none;
  border: 0;
  padding: ${SPACE[0]};
  font: inherit;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`;

/** A tracking link's address, cut short with an ellipsis. */
export const ContextUrl = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 340px;
  color: ${HOME.t400};
`;

/** The card's control, pinned to its top right beside the sentences. */
export const Action = styled.div`
  flex: none;
  margin-left: auto;
`;

/** The sentences as a Lumio summary across the card: on a faint pink→cyan panel, the Lumio mark before the headline. */
export const LumioPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  padding: ${SPACE[12]} ${SPACE[16]} ${SPACE[16]};
  border-radius: 14px;
  border: 1px solid ${LUMIO.summaryBorder};
  background: ${LUMIO.summaryBg};
`;

/** What the numbers are narrowed to (left) and the report download (right), above the summary. */
export const Top = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACE[16]};
  padding: ${SPACE[4]} ${SPACE[4]} ${SPACE[0]};
`;

/** The headline with the Lumio mark before it, the mark on its first line. */
export const LeadLine = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${SPACE[8]};

  > img {
    flex: none;
    margin-top: 4px; /* centres the 20px mark on the headline's first line (20px type, 1.35 leading) */
  }
`;
